import { Eye, RotateCcw, Trash2 } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import {
  clearWhatsNew,
  deleteWhatsNewArchive,
  getWhatsNew,
  listWhatsNewArchive,
  publishWhatsNew,
  revertWhatsNew,
} from "@/features/admin/api";
import type { WhatsNew, WhatsNewArchiveEntry } from "@/features/admin/types";

export default function WhatsNewPage() {
  const [whatsnew, setWhatsNew] = useState<WhatsNew | null>(null);
  const [archive, setArchive] = useState<WhatsNewArchiveEntry[]>([]);
  const [body, setBody] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishTitle, setPublishTitle] = useState("");
  const [clearOpen, setClearOpen] = useState(false);
  const [deletingArchive, setDeletingArchive] = useState<WhatsNewArchiveEntry | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [wn, arc] = await Promise.all([getWhatsNew(), listWhatsNewArchive()]);
      setWhatsNew(wn);
      setBody(wn.current);
      setArchive(arc);
    } catch {
      toast.error("Couldn't load What's New.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handlePublish() {
    if (!publishTitle.trim()) {
      toast.error("An archive title is required.");
      return;
    }
    setBusy(true);
    try {
      await publishWhatsNew(publishTitle.trim(), body);
      setPublishOpen(false);
      setPublishTitle("");
      await refresh();
      toast.success("Published — the popup will reshow for all users.");
    } catch {
      toast.error("Couldn't publish. Add some content first.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevert() {
    setBusy(true);
    try {
      await revertWhatsNew();
      await refresh();
      toast.success("Reverted to the previous entry.");
    } catch {
      toast.error("Couldn't revert.");
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    setBusy(true);
    try {
      await clearWhatsNew();
      setClearOpen(false);
      await refresh();
      toast.success("Cleared.");
    } catch {
      toast.error("Couldn't clear.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteArchive() {
    if (!deletingArchive) return;
    try {
      await deleteWhatsNewArchive(deletingArchive.id);
      setDeletingArchive(null);
      setArchive((a) => a.filter((e) => e.id !== deletingArchive.id));
      toast.success("Archive entry deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Current entry</CardTitle>
            <CardDescription>
              This shows as a popup the next time each user logs in. Publishing archives the
              previous entry and re-shows the popup for everyone.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <RichTextEditor value={body} onChange={setBody} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                <Eye className="size-4" /> Preview
              </Button>
              <Button onClick={() => setPublishOpen(true)} disabled={busy}>
                Save / Publish
              </Button>
              <Button variant="outline" onClick={() => setClearOpen(true)} disabled={busy}>
                Clear out
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Previous entry</CardTitle>
              <CardDescription>The entry replaced at the last publish.</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !whatsnew?.previous}
              onClick={() => void handleRevert()}
            >
              <RotateCcw className="size-4" /> Revert
            </Button>
          </CardHeader>
          <CardContent>
            {whatsnew?.previous ? (
              <SanitizedHtml html={whatsnew.previous} />
            ) : (
              <p className="text-muted-foreground text-sm">No previous entry.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <CardHeader className="p-4">
          <CardTitle>Archive</CardTitle>
          <CardDescription>Published history.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {archive.length === 0 ? (
            <p className="text-muted-foreground p-4 text-sm">No archived entries.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {archive.map((entry) => (
                  <Fragment key={entry.id}>
                    <TableRow>
                      <TableCell className="font-medium">{entry.title}</TableCell>
                      <TableCell>{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Preview"
                            onClick={() =>
                              setExpanded((cur) => (cur === entry.id ? null : entry.id))
                            }
                          >
                            <Eye className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Delete"
                            onClick={() => setDeletingArchive(entry)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === entry.id && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <SanitizedHtml html={entry.body} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          {body ? (
            <SanitizedHtml html={body} />
          ) : (
            <p className="text-muted-foreground text-sm">Nothing to preview yet.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Publish modal */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish What's New</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="publish-title">Archive title</Label>
            <Input
              id="publish-title"
              placeholder="e.g. September 2026 update"
              value={publishTitle}
              onChange={(e) => setPublishTitle(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              The current entry is archived under this title, and the popup reshows for every
              user.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => void handlePublish()} disabled={busy}>
              {busy ? "Publishing…" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear confirm */}
      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear the current popup?</AlertDialogTitle>
            <AlertDialogDescription>
              The What's New popup will stop showing until you publish a new entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleClear()}>Clear</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive delete confirm */}
      <AlertDialog
        open={!!deletingArchive}
        onOpenChange={(o) => !o && setDeletingArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deletingArchive?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDeleteArchive()}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
